import React from 'react';
import { DashboardCard } from '../components/DashboardCard';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Skeleton,
  Box,
} from '@mui/material';
import { useEndReasonTypeData } from '../contexts';
import { EndReasonTypeFilterControls } from './EndReasonTypeFilterControls';

const EndReasonTypeTableContent = () => {
  const { data, loading, error, filter, totalsByYear, grandTotal } = useEndReasonTypeData();

  if (loading) {
    return <Skeleton variant="rounded" height={300} />;
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Typography align="center" color="textSecondary">
        No end reason type data available for selected years
      </Typography>
    );
  }

  const years = [...new Set(data.map((d) => d.year))].sort();
  const endReasonTypes = [...new Set(data.map((d) => d.endReasonTypeName))].sort();

  const renderTableContent = () => {
    if (filter.schoolYears.length === 1) {
      const year = years[0];
      const yearData = data.filter((d) => d.year === year);

      return (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>End Reason Type</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Number of Students
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Number Enrolled
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  % Submitted
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {yearData.map((item) => (
                <TableRow key={item.endReasonTypeId}>
                  <TableCell>{item.endReasonTypeName}</TableCell>
                  <TableCell align="right">{item.studentCount}</TableCell>
                  <TableCell align="right">{item.totalEnrolled}</TableCell>
                  <TableCell align="right">
                    <strong>{item.percentageSubmitted.toFixed(1)}%</strong>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {totalsByYear[0]?.totalStudents}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {totalsByYear[0]?.totalEnrolled}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {totalsByYear[0]?.totalPercentage.toFixed(1)}%
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      );
    } else {
      return (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>End Reason Type</TableCell>
                {years.map((year) => (
                  <TableCell key={year} align="center" sx={{ fontWeight: 'bold' }}>
                    {`${year}-${year + 1}`}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {endReasonTypes.map((endReasonType) => (
                <TableRow key={endReasonType}>
                  <TableCell>{endReasonType}</TableCell>
                  {years.map((year) => {
                    const cellData = data.find(
                      (d) => d.year === year && d.endReasonTypeName === endReasonType
                    );

                    if (!cellData) {
                      return (
                        <TableCell key={year} align="center">
                          -
                        </TableCell>
                      );
                    }

                    return (
                      <TableCell key={year} align="center">
                        {`${cellData.studentCount} (${cellData.percentageSubmitted.toFixed(1)}%)`}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                {years.map((year) => {
                  const yearTotal = totalsByYear.find((t) => t.year === year);
                  if (!yearTotal) {
                    return (
                      <TableCell key={year} align="center" sx={{ fontWeight: 'bold' }}>
                        -
                      </TableCell>
                    );
                  }

                  return (
                    <TableCell key={year} align="center" sx={{ fontWeight: 'bold' }}>
                      {`${yearTotal.totalStudents} (${yearTotal.totalPercentage.toFixed(1)}%)`}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      );
    }
  };

  return (
    <Box sx={{ height: '100%' }}>
      <EndReasonTypeFilterControls />
      {renderTableContent()}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Note: Percentages based on total enrolled students per year
      </Typography>
    </Box>
  );
};

export const EndReasonTypeTableCard = () => (
  <DashboardCard title="End Reason Type">
    <EndReasonTypeTableContent />
  </DashboardCard>
);